import React from 'react';
import { Link } from 'react-router-dom';
import ReminderModal from '../components/ReminderModal';
import { useTheme } from '../context/ThemeContext';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import {
  ChartBarIcon,
  LanguageIcon,
  AcademicCapIcon,
  HeartIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: ChartBarIcon,
    gradient: 'from-sky-500 to-blue-600',
    bg: 'bg-sky-50',
    title: 'Longitudinal Trending',
    description:
      'Track A1C, cholesterol, kidney function, and more across every visit. Get alerted when a value is trending the wrong way — even if it\'s still technically "in range."',
  },
  {
    icon: AcademicCapIcon,
    gradient: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50',
    title: 'Calibrated to You',
    description:
      'Set your explanation level once — from simple everyday language to full clinical detail. Every explanation is tuned to your health literacy, not a fixed grade level.',
  },
  {
    icon: LanguageIcon,
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    title: 'Your Language',
    description:
      'Explanations in 12 languages including Haitian Creole, Vietnamese, Arabic, and more — not just European languages. Plain language, in the language you think in.',
  },
  {
    icon: HeartIcon,
    gradient: 'from-rose-500 to-pink-600',
    bg: 'bg-rose-50',
    title: 'Emotional & Cultural Context',
    description:
      'Sensitive results about cancer, mental health, or chronic conditions are framed with empathy and cultural awareness — not cold clinical text.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    gradient: 'from-amber-500 to-orange-600',
    bg: 'bg-amber-50',
    title: 'Comprehension Verification',
    description:
      'A short quiz after each explanation confirms you actually understood — not just that the words were simple. Know you\'re ready before your next appointment.',
  },
  {
    icon: ArrowPathIcon,
    gradient: 'from-indigo-500 to-blue-600',
    bg: 'bg-indigo-50',
    title: 'Close the Loop',
    description:
      'Get urgency-flagged next steps, a list of questions to ask your doctor, and a one-click shareable summary to send back to your care team.',
  },
];

const languages = [
  'English', 'Spanish', 'French', 'Haitian Creole',
  'Vietnamese', 'Arabic', 'Chinese', 'Portuguese',
  'Korean', 'Russian', 'Hindi', 'Tagalog',
];

const stats = [
  { value: '12', label: 'Languages supported' },
  { value: '30', label: 'Day free trial' },
  { value: '< 60s', label: 'Analysis time' },
  { value: '100%', label: 'Private & encrypted' },
];

export default function LandingPage() {
  const { dark, toggle: toggleDark } = useTheme();
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200">
      <ReminderModal />

      {/* Nav */}
      <nav className="border-b border-slate-100 dark:border-slate-800 sticky top-0 bg-white/90 dark:bg-slate-950/90 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center">
            <img src={dark ? '/logo-dark.svg?v=3' : '/logo.svg?v=3'} alt="HealthClear AI" className="h-9 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleDark}
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-sm font-medium"
            >
              {dark ? <SunIcon className="w-5 h-5 shrink-0" /> : <MoonIcon className="w-5 h-5 shrink-0" />}
              <span>{dark ? 'Light mode' : 'Dark mode'}</span>
            </button>
            <Link
              to="/login"
              className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white px-3 py-1.5"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
            >
              Get started free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-sky-50/40 to-white dark:from-slate-900 dark:via-slate-900/40 dark:to-slate-950">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-sky-100 rounded-full opacity-50 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-blue-100 rounded-full opacity-40 blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white dark:bg-slate-800 text-sky-700 dark:text-sky-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-sky-200 dark:border-sky-800 shadow-sm mb-6">
            <ShieldCheckIcon className="w-3.5 h-3.5" />
            Built for patients, not providers
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight">
            Understand your lab results.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-blue-600">
              In your language. At your level.
            </span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
            HealthClear AI turns confusing medical records into plain-language explanations —
            personalized to how you read, what language you speak, and what you need to do next.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-semibold px-7 py-3.5 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 transform"
            >
              Create free account
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold px-7 py-3.5 rounded-xl transition-colors shadow-sm"
            >
              Sign in
            </Link>
          </div>

          <p className="mt-4 text-xs text-slate-400 flex items-center justify-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" /> 30-day free trial</span>
            <span className="flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" /> No credit card required</span>
            <span className="flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" /> Cancel anytime</span>
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{s.value}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-slate-50 dark:bg-slate-900 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
              Everything missing from today's health apps
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-3 max-w-xl mx-auto">
              Most apps give you a simplified snapshot and nothing else. HealthClear AI adapts,
              tracks, and closes the loop.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${f.gradient} shadow-sm`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">How it works</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">From upload to action in under a minute.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 relative">
          {/* Connecting line (desktop only) */}
          <div className="hidden sm:block absolute top-6 left-[calc(16.6%+1.5rem)] right-[calc(16.6%+1.5rem)] h-0.5 bg-sky-100 dark:bg-sky-900" />
          {[
            { step: '1', title: 'Upload your report', desc: 'Drop a PDF or paste text from any lab provider. We extract all values automatically.' },
            { step: '2', title: 'Get your explanation', desc: 'AI explains every result in your language, at your literacy level, with sensitivity for difficult findings.' },
            { step: '3', title: 'Take action', desc: 'Review urgency flags, get questions for your doctor, take a comprehension quiz, and share a summary with your care team.' },
          ].map((s) => (
            <div key={s.step} className="text-center relative">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4 shadow-md relative z-10">
                {s.step}
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{s.title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust / Security section */}
      <section className="bg-slate-50 dark:bg-slate-900 py-16 border-y border-slate-100 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LockClosedIcon className="w-5 h-5 text-slate-400" />
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">Your privacy is non-negotiable</h2>
          </div>
          <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto text-sm leading-relaxed mb-8">
            All data is encrypted at rest and in transit. We follow HIPAA-aligned practices,
            never sell your data, and you can delete your account and all associated records at any time.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Encrypted at rest & in transit',
              'HIPAA-aligned practices',
              'No data selling, ever',
              'Delete your data anytime',
            ].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                <ShieldCheckIcon className="w-3.5 h-3.5 text-sky-500" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="bg-slate-900 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-slate-400 text-sm font-medium uppercase tracking-widest mb-5">
            Available in 12 languages
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {languages.map((lang) => (
              <span
                key={lang}
                className="bg-slate-800 text-slate-300 text-sm px-3 py-1.5 rounded-full border border-slate-700 hover:border-slate-500 hover:text-white transition-colors"
              >
                {lang}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-sky-600 to-indigo-600 py-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Your results. Finally explained.
          </h2>
          <p className="text-sky-100 mt-3 mb-8 text-lg">
            Free to use. No credit card required. Start understanding your health today.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white hover:bg-sky-50 text-sky-700 font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform text-base"
          >
            Get started free
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
          <p className="mt-4 text-sky-200 text-sm">No credit card · Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 dark:border-slate-800 py-8 text-center">
        <p className="text-xs text-slate-400">
          © 2026 HealthClear AI · For informational purposes only · Not a substitute for medical advice
        </p>
        <p className="text-xs text-slate-400 mt-2">
          6900 Tavistock Lakes Blvd. Suite 400, Orlando FL, 32827 · <a href="tel:6893292013" className="hover:text-slate-600">689-329-2013</a>
        </p>
        <p className="text-xs text-slate-400 mt-2">
          <a href="/privacy" className="hover:text-slate-600 underline">Privacy Policy</a>
          {' · '}
          <a href="/terms" className="hover:text-slate-600 underline">Terms of Service</a>
          {' · '}
          All rights reserved.
        </p>
      </footer>

    </div>
  );
}
